import { Paper, Typography, Divider, Stack, LinearProgress, FormControlLabel, Checkbox, Box } from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import { CustomFieldDto, CustomFieldOptionDto, ReportFieldFilterInput, ReportParametersInput, SystemFieldId } from 'gql';
import React, { useMemo } from 'react';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { FilterInput } from '../FilterInput';
import { IconButtonTooltip } from 'components/IconButtonTooltip';

type CustomField = Pick<CustomFieldDto, 'id' | 'name' | 'customFieldType' | 'systemFieldId'> & {
  options: Pick<CustomFieldOptionDto, 'id' | 'name'>[];
};

interface Props {
  fields: CustomField[];
  loading?: boolean;
  disabled?: boolean;
}

export const ReportOutreachWorkersSection: React.FC<Props> = ({ fields, disabled, loading }) => {
  const { formatMessage } = useIntl();

  const { control } = useFormContext<ReportParametersInput>();
  const { fields: fieldFilters, append, update } = useFieldArray({ control, name: 'filters.customFieldFilters' });

  const useOrLogic = useWatch({ control, name: 'filters.useOutreachWorkersOrLogic' });

  const outreachWorkerFields = useMemo(() =>
    fields.filter(f =>
      f.systemFieldId != null && (
        f.systemFieldId === SystemFieldId.OutreachWorker ||
        f.systemFieldId === SystemFieldId.AdditionalWorkers
      )
    ), [fields]);

  const outreachWorkerField = useMemo(() =>
    outreachWorkerFields.find(f => f.systemFieldId === SystemFieldId.OutreachWorker),
  [outreachWorkerFields]);

  const additionalWorkersField = useMemo(() =>
    outreachWorkerFields.find(f => f.systemFieldId === SystemFieldId.AdditionalWorkers),
  [outreachWorkerFields]);

  const onChange = (customFieldFilterInput: ReportFieldFilterInput) => {
    const index = fieldFilters.findIndex(f => f.customFieldId === customFieldFilterInput.customFieldId);
    if (index === -1) {
      append(customFieldFilterInput);
    } else {
      update(index, customFieldFilterInput);
    }
  };

  const handleOrLogicChange = (checked: boolean) => {
    // When enabling OR logic, clear the additional workers filter
    if (checked && additionalWorkersField) {
      const additionalWorkersIndex = fieldFilters.findIndex(f => f.customFieldId === additionalWorkersField.id);
      if (additionalWorkersIndex !== -1) {
        update(additionalWorkersIndex, { customFieldId: additionalWorkersField.id, systemFieldId: additionalWorkersField.systemFieldId });
      }
    }
  };

  if (outreachWorkerFields.length === 0) {
    return null;
  }

  return (
    <Paper>
      <Typography variant='h6' p={2} pb={1}>{formatMessage({ id: 'Outreach workers' })}</Typography>

      <LinearProgress style={{ visibility: loading ? 'visible' : 'hidden' }} />
      <Divider />

      <Stack p={2} spacing={0.5}>
        {outreachWorkerField && (
          <FilterInput
            key={outreachWorkerField.id}
            customField={outreachWorkerField}
            value={fieldFilters.find(f => f.customFieldId === outreachWorkerField.id) ?? { customFieldId: outreachWorkerField.id, systemFieldId: outreachWorkerField.systemFieldId }}
            onChange={onChange}
            disabled={disabled}
          />
        )}

        <Controller
          control={control}
          name='filters.useOutreachWorkersOrLogic'
          render={({ field }) => (
            <Box display="flex" alignItems="center" justifyContent="flex-end">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value ?? false}
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                      handleOrLogicChange(e.target.checked);
                    }}
                    disabled={disabled}
                  />
                }
                label={formatMessage({ id: 'Search also in the additional workers' })}
                sx={{ marginRight: 0.5 }}
              />
              <IconButtonTooltip
                tooltip={formatMessage({ id: 'Select this checkbox to include activities where the selected user is an outreach worker OR an additional worker.' })}
                size="small"
              >
                <HelpOutline fontSize="small" />
              </IconButtonTooltip>
            </Box>
          )}
        />

        {additionalWorkersField && !useOrLogic && (
          <FilterInput
            key={additionalWorkersField.id}
            customField={additionalWorkersField}
            value={fieldFilters.find(f => f.customFieldId === additionalWorkersField.id) ?? { customFieldId: additionalWorkersField.id, systemFieldId: additionalWorkersField.systemFieldId }}
            onChange={onChange}
            disabled={disabled}
          />
        )}
      </Stack>
    </Paper>
  );
};